import { UserFieldOption } from 'utils/api/modules/user/user.types';

import { EntityModules, ProtectorType } from 'core/constants';

import { FieldType } from 'stores/EntityFields/types';

import { eventFieldKind, EventFieldKind } from './kinds';

export const IN_PIPELINE_GROUP = 'Relevant in this pipeline';
export const OTHER_FIELDS_GROUP = 'All other fields';

/** Where a condition row's values come from, which is not knowable from the kind alone. */
export type ValueSource =
  | { kind: 'options'; options: UserFieldOption[] }
  | { kind: 'search'; lookupType?: EntityModules }
  | { kind: 'user' }
  | { kind: 'text' }
  | { kind: 'none' };

export interface PickerField {
  key: string;
  name: string;
  kind: EventFieldKind;
  protectorType: ProtectorType;
  group: string;
}

export interface EventFieldCatalogue {
  pickerFields: PickerField[];
  labels: Record<string, string>;
  valueSources: Record<string, ValueSource>;
}

const UNPICKABLE_TYPES: ReadonlySet<ProtectorType> = new Set([
  ProtectorType.Pipeline,
  ProtectorType.Stage,
  ProtectorType.Image,
  ProtectorType.Section,
  ProtectorType.Attachment,
  ProtectorType.Signature,
  ProtectorType.RepeatingGroup,
  ProtectorType.YesNoNa,
]);

const toOptions = (field: FieldType): UserFieldOption[] =>
  (field.enum ?? []).map((option, index) => ({
    uuid: option.key,
    name: option.value,
    isActive: true,
    ordering: index,
  }));

const valueSourceFor = (field: FieldType): ValueSource => {
  if (field.protectorType === ProtectorType.Lookup) {
    return { kind: 'search', lookupType: field.lookupType };
  }
  if (field.protectorType === ProtectorType.User) {
    return { kind: 'user' };
  }
  if (eventFieldKind(field.protectorType) === 'date') {
    return { kind: 'none' };
  }
  return eventFieldKind(field.protectorType) === 'option'
    ? { kind: 'options', options: toOptions(field) }
    : { kind: 'text' };
};

export const buildEventFieldCatalogue = (
  fields: FieldType[],
  inPipelineKeys: ReadonlySet<string>
): EventFieldCatalogue => {
  const pickable = fields.filter((field) => !UNPICKABLE_TYPES.has(field.protectorType) && !field.readonly);

  const byName = (a: PickerField, b: PickerField): number => a.name.localeCompare(b.name, 'en');

  const toPickerField = (field: FieldType, group: string): PickerField => ({
    key: field.id,
    name: field.name,
    kind: eventFieldKind(field.protectorType),
    protectorType: field.protectorType,
    group,
  });

  const pickerFields: PickerField[] = [
    ...pickable
      .filter((field) => inPipelineKeys.has(field.id))
      .map((field) => toPickerField(field, IN_PIPELINE_GROUP))
      .sort(byName),
    ...pickable
      .filter((field) => !inPipelineKeys.has(field.id))
      .map((field) => toPickerField(field, OTHER_FIELDS_GROUP))
      .sort(byName),
  ];

  return {
    pickerFields,
    labels: Object.fromEntries(pickable.map((field) => [field.id, field.name])),
    valueSources: Object.fromEntries(pickable.map((field) => [field.id, valueSourceFor(field)])),
  };
};

export const valueLabel = (catalogue: EventFieldCatalogue, attribute: string, value: string): string => {
  const source = catalogue.valueSources[attribute];
  if (source?.kind !== 'options') {
    return value;
  }
  return source.options.find((option) => option.uuid === value)?.name ?? value;
};
